import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { type EntitlementKey, type ZendeskPlatformModel } from "@doitintl/cmp-models";
import { Box, Link, Stack, Typography } from "@mui/material";

import { useAvaDialogContext } from "../../Components/Ava2/AvaDialogContextProvider";
import { useCustomerContext } from "../../Context/CustomerContext";
import { useIsFeatureEntitled, useTier } from "../../Context/TierProvider";
import { isCustomerInPresentationMode } from "../../Context/useCustomerOrPresentationModeCustomer";
import { useGetCustomerContractAndAssetDetails } from "../hooks/useGetCustomerContractAndAssetDetails";
import { useIsDCIEssentialsTier } from "../hooks/useIsDCIEssentialsTier";
import { isDoitConsoleBaseService } from "../utils";
import SupportItemIcon from "./SupportItemIcon";

type Props = {
  overrideIfDisabled: boolean;
  zendPlatform: ZendeskPlatformModel;
};

const SupportPlatformMenuItemContent = ({ zendPlatform, overrideIfDisabled }: Props) => {
  const { customerOrPresentationModeCustomer: customer, customer: presentationDataCustomer } = useCustomerContext({
    allowNull: true,
  });
  const isInPresentationMode = presentationDataCustomer && isCustomerInPresentationMode(presentationDataCustomer);

  const showAlwaysEnabled = zendPlatform.asset === "perfectscale" || isInPresentationMode;

  const { tiers } = useTier();
  const { customerDoesNotHaveAssets, isPlatformWithoutBillingAccount, isStandaloneAppWithoutContractOrAssets } =
    useGetCustomerContractAndAssetDetails();
  const { t } = useTranslation("services");
  const isDCIEssentialsTier = useIsDCIEssentialsTier();

  const { handleCloseAvaDialog } = useAvaDialogContext();

  const history = useHistory();

  const platformName = zendPlatform.asset.replace("-project", "");
  const platformEntitlementKey = `support:ticket:menu:platform:${platformName}`;
  const isAllPlatformsEntitled = useIsFeatureEntitled("support:ticket:menu:platform:all");
  const isThisPlatformEntitled = useIsFeatureEntitled(platformEntitlementKey as EntitlementKey);
  const featureUnlocked = isDoitConsoleBaseService(platformName) || isAllPlatformsEntitled || isThisPlatformEntitled;

  const isMissingBillingAccount = isPlatformWithoutBillingAccount(zendPlatform.asset);
  const skipAssetCheck = platformName === "office-365" || platformName === "g-suite";

  const isMissingAssets = customerDoesNotHaveAssets(zendPlatform.asset) && !skipAssetCheck;

  const missingBillingAcctOrAssetsText = isMissingBillingAccount
    ? t("SupportPlatformMennuItemContent.connectAccount")
    : t("SupportPlatformMennuItemContent.createAsset");

  const disableAppPlatformLink = isStandaloneAppWithoutContractOrAssets(zendPlatform.asset);

  const isMissingAsset = (isMissingBillingAccount || isMissingAssets) && zendPlatform.asset !== "perfectscale";

  const renderTypography = () => {
    const enabledLink = (
      <Typography
        sx={{
          ml: 1,
        }}
      >
        {zendPlatform.title}
      </Typography>
    );

    if (isDCIEssentialsTier) {
      return enabledLink;
    }

    // preserve original behavior for customers with no subscription configurations
    if (!tiers) {
      return (
        <Typography component={Box} variant="body1" sx={{ ml: 1 }}>
          {zendPlatform.title}
        </Typography>
      );
    }

    const disabledMainTextTypography = (
      <Typography
        component={Box}
        variant="body1"
        sx={{
          color: "text.disabled",
        }}
      >
        {zendPlatform.title}
      </Typography>
    );

    // disabled - not available
    if ((!featureUnlocked || disableAppPlatformLink) && !overrideIfDisabled && !showAlwaysEnabled) {
      return (
        <Stack
          direction="row"
          sx={{
            width: "100%",
            justifyContent: "space-between",
            ml: 1,
          }}
        >
          {disabledMainTextTypography}
        </Stack>
      );
    }

    // disabled - missing billing account or relevant assets
    if (isMissingAsset && !showAlwaysEnabled) {
      return (
        <Stack
          direction="row"
          onClick={() => {
            history.push(`/customers/${customer?.id}/integrate/assets/${zendPlatform.asset.replace("-project", "")}`);
          }}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            ml: 1,
          }}
        >
          {disabledMainTextTypography}
          <Link
            variant="body1"
            underline="none"
            sx={{ cursor: "pointer", pointerEvents: "auto" }}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseAvaDialog();
              history.push(`/customers/${customer?.id}/integrate/assets/${zendPlatform.asset.replace("-project", "")}`);
            }}
            data-cy="missing-asset-link"
          >
            {missingBillingAcctOrAssetsText}
          </Link>
        </Stack>
      );
    }

    // enabled
    return enabledLink;
  };
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        width: "100%",
      }}
    >
      <SupportItemIcon
        platformValue={zendPlatform.value}
        disabled={
          !!tiers &&
          (!featureUnlocked || isMissingAsset) &&
          !isDCIEssentialsTier &&
          !overrideIfDisabled &&
          !showAlwaysEnabled
        }
        onClick={
          !!tiers && isMissingAsset && !disableAppPlatformLink && !isDCIEssentialsTier
            ? () => {
                history.push(
                  `/customers/${customer?.id}/integrate/assets/${zendPlatform.asset.replace("-project", "")}`
                );
              }
            : undefined
        }
      />
      {renderTypography()}
    </Stack>
  );
};

export default SupportPlatformMenuItemContent;
